 
import { Claim } from 'domains/billing';
import { isClaimFinalized } from 'domains/claims/status';
import { Cpt, formatCptCode } from 'domains/cpt';
import { Therapist } from 'domains/therapists';
import { ServiceLineForDisplay, SuperbillDisplayMode } from '../ui';
import { getProviderAddressLine2 } from './addresses';

interface CalculateInsuranceProps {
  claim: Claim;
  therapist: Therapist;
  cptData: Cpt | undefined;
  paidByPatient: number;
}

export const calculateInsuranceProps = ({ claim, therapist, cptData, paidByPatient }: CalculateInsuranceProps) => {
  const {
    rendering_provider,
    billing_provider,
    referring_provider,
    patient,
    service_facility,
    claims,
    date_of_service,
    subscriber_primary,
  } = claim;

  const serviceLines = claims[0].service_lines.map((serviceLine): ServiceLineForDisplay => {
    return {
      serviceLineID: serviceLine.service_line_id,
      cptCode: cptData && formatCptCode(cptData),
      cptDescription: cptData && cptData.description,
      charge: serviceLine.charge_amount_cents ?? 0,
      allowed: serviceLine.allowed_amount_cents ?? 0,
      paidByPatient,
    };
  });

  const totalCharges = serviceLines.reduce((total, x) => total + x.charge, 0);
  const totalAllowed = serviceLines.reduce((total, x) => total + x.allowed, 0);
  const totalPaidByPatient = serviceLines.reduce((total, x) => total + x.paidByPatient, 0);

  return {
    mode: SuperbillDisplayMode.Insurance,
    claimFinalized: isClaimFinalized(claims[0].status),
    renderingProviderName: `${rendering_provider.first_name} ${rendering_provider.last_name}`,
    renderingProviderLicenseNumber: therapist.licenseNumbers,
    renderingProviderNpi: rendering_provider.npi,
    billingProviderName: billing_provider.organization_name,
    billingProviderTaxID: billing_provider.tax_id,
    billingProviderNpi: billing_provider.npi,
    billingAddressLines: [
      billing_provider.address?.address1,
      billing_provider.address?.address2,
      billing_provider.address && getProviderAddressLine2(billing_provider.address),
    ].filter(Boolean) as string[],
    serviceAddressLines: [
      service_facility?.address?.address1,
      service_facility?.address?.address2,
      service_facility?.address && getProviderAddressLine2(service_facility?.address),
    ].filter(Boolean) as string[],
    patientName: `${patient.first_name} ${patient.last_name}`,
    patientDateOfBirth: patient.date_of_birth,
    patientAddressLines: [
      patient.address?.address1,
      patient.address?.address2,
      patient.address && getProviderAddressLine2(patient.address),
    ].filter(Boolean) as string[],
    referringPhysicianName: `${referring_provider.first_name} ${referring_provider.last_name}`,
    serviceLines,
    totalCharges,
    totalAllowed,
    totalPaidByPatient,
    balanceDue: claim.shownBalanceInCents,
    dateOfService: date_of_service,
    therapistName: `${therapist.firstName} ${therapist.lastName}, CCC-SLP`,
    insuredName: `${subscriber_primary?.first_name} ${subscriber_primary?.last_name}`,
    insurancePlan: subscriber_primary?.insurance_card?.payer_name,
    memberID: subscriber_primary?.insurance_card?.member_id,
  };
};
